import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getRoot, ElementNode} from 'lexical';
import {FC, useEffect, useState} from 'react';
import {$generateNodesFromDOM} from '@lexical/html';
import {useEmailContext} from '../../../../../context';
import {generatingHtmlString} from '../../ToolbarPlugin/utils';
import {noop} from '@priority-software/priority-style-react';
import {HorizontalRuleNode} from '@lexical/react/LexicalHorizontalRuleNode';

export interface IInsertHtmlAndImagesPlugin {
    htmlString: string;
    isFromTextEditor?: boolean;
}

export const InsertHtmlAndImagesPlugin: FC<IInsertHtmlAndImagesPlugin> = ({
    htmlString,
    isFromTextEditor
}) => {
    const [editor] = useLexicalComposerContext();
    const [isMounted, setIsMounted] = useState(false);

    const {
        states: {language, isDirty},
        handlers: {setIsDirty}
    } = useEmailContext();

    useEffect(() => {
        editor.update(() => {
            // We need to use `isMounted` as a state to make sure that the logic inside`registerUpdateListener`
            // listener executes after the default html initialization
            if (isMounted) return;

            if (htmlString) {
                const encodedHtmlString = generatingHtmlString(htmlString, isFromTextEditor);
                const parser = new DOMParser();
                const dom = parser.parseFromString(encodedHtmlString, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);

                const root = $getRoot();
                root.clear();

                const validNodes = nodes.filter((node): node is ElementNode => {
                    return node instanceof ElementNode || node instanceof HorizontalRuleNode;
                });
                root.append(...validNodes);
            }
            setIsMounted(true);
        });
    }, [editor, htmlString, language, isMounted]);

    useEffect(() => {
        // Handle isDirty flag (first meaningful content change (text updates or text formatting ))
        if (isMounted && !isDirty) {
            const removeTextContentListener = editor.registerUpdateListener(updateData => {
                if (updateData.dirtyElements.size > 0) {
                    setIsDirty(true);
                }
            });
            return () => removeTextContentListener();
        }
        return noop;
    }, [editor, isMounted, isDirty, setIsDirty]);

    return null;
};
